import {
  BedrockAgentClient,
  StartIngestionJobCommand,
} from '@aws-sdk/client-bedrock-agent';
import {
  BedrockAgentRuntimeClient,
  RetrieveCommand,
} from '@aws-sdk/client-bedrock-agent-runtime';
import {
  CopyObjectCommand,
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  PutObjectCommandInput,
  S3Client,
} from '@aws-sdk/client-s3';
import { SendEmailCommand, SESClient } from '@aws-sdk/client-ses';
import { SendMessageCommand, SQSClient } from '@aws-sdk/client-sqs';
import getConfig from '../../common/config';
import slack from '../../server/libs/slack';
import logger from './logger';

const { serverRuntimeConfig } = getConfig();

/**
 * 接続確立の上限時間（ミリ秒）
 *
 * DNS/TCP/TLSの確立だけでなく、コネクションプールの空き待ちにも適用される。
 * SDKの既定値は0（無効）のため、この設定がないとAWS側が無応答になった際に
 * `client.send()` が永久に待ち続け、処理されないリクエストがプロセス内に滞留し続ける。
 */
const CONNECTION_TIMEOUT_MS = 3_000;

/**
 * 通信が無反応のまま経過できる上限時間（ミリ秒）
 *
 * ソケットのアイドル時間に対するタイムアウトであり、レスポンス待ちだけでなく
 * ボディの送受信中も有効。転送が続いている限りこの時間を超えても中断されない。
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * S3の無反応の上限時間（ミリ秒）
 *
 * 画像・動画・音声の配信や大きなファイルのアップロードは転送そのものに時間がかかるため、
 * {@link REQUEST_TIMEOUT_MS} より長くとる。ALBのアイドルタイムアウトと同じ60秒に合わせている。
 */
const S3_REQUEST_TIMEOUT_MS = 60_000;

/**
 * Bedrockの無反応の上限時間（ミリ秒）
 *
 * ナレッジベース検索＋リランクは応答までに時間がかかるため長めにとる。
 */
const BEDROCK_REQUEST_TIMEOUT_MS = 30_000;

/**
 * S3への同時接続数の上限
 *
 * 動画・音声のレンジ配信は1リクエストが配信中ずっと1ソケットを占有するため、
 * SDKの既定値（50）では同時視聴数が増えたときに枯渇しやすい。
 */
const S3_MAX_SOCKETS = 200;

/**
 * 各AWSクライアント共通のHTTPハンドラ設定
 *
 * SDKの既定ではタイムアウトが無効なため、すべてのクライアントに明示的に設定する。
 * オブジェクトで渡すとSDK内部で `NodeHttpHandler` が生成されるため、
 * `@smithy/node-http-handler` へ直接依存する必要はない。
 */
const defaultRequestHandlerOptions = {
  connectionTimeout: CONNECTION_TIMEOUT_MS,
  requestTimeout: REQUEST_TIMEOUT_MS,
};

const s3Client = new S3Client({
  region: serverRuntimeConfig.s3Region,
  requestHandler: {
    ...defaultRequestHandlerOptions,
    requestTimeout: S3_REQUEST_TIMEOUT_MS,
    httpsAgent: { maxSockets: S3_MAX_SOCKETS },
  },
  maxAttempts: 2, // リトライで待ち行列を太らせない
});

const sesClient = new SESClient({
  region: serverRuntimeConfig.sesRegion,
  requestHandler: defaultRequestHandlerOptions,
});

const sqsClient = new SQSClient({
  region: serverRuntimeConfig.sqsRegion,
  requestHandler: defaultRequestHandlerOptions,
});

const bedrockAgentClient = new BedrockAgentClient({
  region: serverRuntimeConfig.bedrockRegion,
  requestHandler: defaultRequestHandlerOptions,
});

const bedrockAgentRuntimeClient = new BedrockAgentRuntimeClient({
  region: serverRuntimeConfig.bedrockRegion,
  requestHandler: {
    ...defaultRequestHandlerOptions,
    requestTimeout: BEDROCK_REQUEST_TIMEOUT_MS,
  },
});

export async function getS3Object(key: string, options?: { range?: string }) {
  return s3Client.send(
    new GetObjectCommand({
      Bucket: serverRuntimeConfig.s3Bucket,
      Key: key,
      Range: options?.range,
    }),
  );
}

export async function putS3Object(
  key: string,
  body: PutObjectCommandInput['Body'],
  options?: { contentType?: string; contentEncoding?: string },
) {
  return s3Client.send(
    new PutObjectCommand({
      Bucket: serverRuntimeConfig.s3Bucket,
      Key: key,
      Body: body,
      ContentType: options?.contentType,
      ContentEncoding: options?.contentEncoding,
    }),
  );
}

export async function listS3ObjectKeys(prefix: string): Promise<string[]> {
  const keys: string[] = [];
  let continuationToken: string | undefined;

  do {
    const response = await s3Client.send(
      new ListObjectsV2Command({
        Bucket: serverRuntimeConfig.s3Bucket,
        Prefix: prefix,
        ContinuationToken: continuationToken,
      }),
    );

    keys.push(
      ...(response.Contents?.flatMap(({ Key }) => (Key ? [Key] : [])) ?? []),
    );
    continuationToken = response.IsTruncated
      ? response.NextContinuationToken
      : undefined;
  } while (continuationToken);

  return keys;
}

export async function copyS3Object(sourceKey: string, destinationKey: string) {
  const encodedSourceKey = sourceKey
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');

  return s3Client.send(
    new CopyObjectCommand({
      Bucket: serverRuntimeConfig.s3Bucket,
      CopySource: `${serverRuntimeConfig.s3Bucket}/${encodedSourceKey}`,
      Key: destinationKey,
    }),
  );
}

export async function deleteS3Object(key: string) {
  return s3Client.send(
    new DeleteObjectCommand({
      Bucket: serverRuntimeConfig.s3Bucket,
      Key: key,
    }),
  );
}

export async function putS3KnowledgeBaseText(key: string, text: string) {
  return await putS3Object(`knowledgebase/${key}`, text, {
    contentType: 'text/plain; charset=utf-8',
    contentEncoding: 'utf-8',
  });
}

export async function startIngestionJob() {
  return await bedrockAgentClient.send(
    new StartIngestionJobCommand({
      knowledgeBaseId: serverRuntimeConfig.bedrockKnowledgeBaseId,
      dataSourceId: serverRuntimeConfig.bedrockKnowledgeBaseDataSourceId,
    }),
  );
}

export async function retrieve({
  query,
  numberOfResults = 100,
}: {
  query: string;
  numberOfResults?: number;
}) {
  return await bedrockAgentRuntimeClient.send(
    new RetrieveCommand({
      knowledgeBaseId: serverRuntimeConfig.bedrockKnowledgeBaseId,
      retrievalQuery: {
        text: query,
      },
      retrievalConfiguration: {
        vectorSearchConfiguration: {
          numberOfResults,
          rerankingConfiguration: {
            type: 'BEDROCK_RERANKING_MODEL',
            bedrockRerankingConfiguration: {
              modelConfiguration: {
                modelArn:
                  serverRuntimeConfig.bedrockKnowledgeBaseRerankModelArn,
              },
            },
          },
        },
      },
    }),
  );
}

export async function sendSqsMessage(messageBody: string) {
  return await sqsClient.send(
    new SendMessageCommand({
      QueueUrl: serverRuntimeConfig.sqsQueueNamePpt,
      MessageBody: messageBody,
    }),
  );
}

export async function sendSqsMessageForCreateToc(articleId: string) {
  const messageBody = { article_id: articleId };
  try {
    const result = await sqsClient.send(
      new SendMessageCommand({
        QueueUrl: serverRuntimeConfig.sqsQueueCreateToc,
        MessageBody: JSON.stringify(messageBody),
      }),
    );
    slack.sendToSlack(
      `目次作成リクエストのエンキューに成功しました: article_id=${articleId}`,
      '',
    );
    return result;
  } catch (e) {
    logger.error(
      `目次作成リクエストのエンキューに失敗しました: article_id=${articleId}; e=${e}`,
    );
    slack.sendToSlack(
      `目次作成リクエストのエンキューに失敗しました: article_id=${articleId}; e=${e}`,
      '',
    );
  }
}

export async function sendSqsMessageForUploadKnowledgeBase(articleId: string) {
  const messageBody = { article_id: articleId };
  try {
    const result = await sqsClient.send(
      new SendMessageCommand({
        QueueUrl: serverRuntimeConfig.sqsQueueUploadKnowledgeBase,
        MessageBody: JSON.stringify(messageBody),
      }),
    );
    slack.sendToSlack(
      `ナレッジベースアップロードリクエストのエンキューに成功しました: article_id=${articleId}`,
      '',
    );
    return result;
  } catch (e) {
    logger.error(
      `ナレッジベースアップロードリクエストのエンキューに失敗しました: article_id=${articleId}; e=${e}`,
    );
    slack.sendToSlack(
      `ナレッジベースアップロードリクエストのエンキューに失敗しました: article_id=${articleId}; e=${e}`,
      '',
    );
  }
}

export async function sendPlainTextEmail(
  to: string,
  from: string,
  subject: string,
  body: string,
) {
  // SEE: https://docs.aws.amazon.com/sdk-for-javascript/v3/developer-guide/ses-examples-sending-email.html
  return await sesClient.send(
    new SendEmailCommand({
      Destination: {
        ToAddresses: [to],
      },
      Message: {
        Body: {
          Text: {
            Charset: 'UTF-8',
            Data: body,
          },
        },
        Subject: {
          Charset: 'UTF-8',
          Data: subject,
        },
      },
      Source: from,
    }),
  );
}

export async function getJsonFileFromS3(s3Key: string) {
  const object = await getS3Object(s3Key).catch((e) => {
    if (e.name === 'NoSuchKey') {
      return null;
    }
    throw e;
  });

  const contentType = object?.ContentType?.toLowerCase() ?? '';
  const isJsonContentType = contentType.startsWith('application/json');

  if (!object || !object.Body || !isJsonContentType) {
    return null;
  }

  const body = await object.Body.transformToString();
  try {
    return JSON.parse(body);
  } catch (e) {
    logger.error(`JSONファイルのパースに失敗しました:s3Key=${s3Key}; e=${e}`);

    throw e;
  }
}
